"use client";

import { Icon } from "@/components/ui/Icon";
import { motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { CHAUFFEUR_HUB } from "./hubDict";

type SeoContentSectionProps = { locale?: Locale };

const WHY_ICONS = [
  "ph:seal-check",
  "ph:car",
  "ph:calendar-check",
  "ph:clock",
] as const;

const SeoContentSection = ({
  locale = "fr",
}: SeoContentSectionProps): JSX.Element => {
  const d = tr(CHAUFFEUR_HUB, locale).seoContent;
  return (
    <section className="relative overflow-hidden bg-white py-16 text-black">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/[0.02] via-transparent to-black/[0.02]" />
      <div className="pointer-events-none absolute -right-16 top-10 h-52 w-52 rounded-full bg-primary/10 blur-[90px]" />
      <div className="relative mx-auto max-w-[90rem] px-6 md:px-8">
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.4 }}
          className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
        >
          {d.eyebrow}
        </motion.p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="mb-3 font-space-age text-xl tracking-[0.2em]"
        >
          {d.heading}
        </motion.h2>
        <div className="mb-6 grid gap-3 sm:grid-cols-3">
          {d.metrics.map((metric, idx) => (
            <motion.div
              key={metric.k}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.35, delay: idx * 0.06 }}
              whileHover={{ y: -2 }}
              className="rounded-xl border border-black/10 bg-white px-4 py-3"
            >
              <p className="text-xs uppercase tracking-[0.16em] text-black/55">
                {metric.k}
              </p>
              <p className="mt-1 text-sm font-semibold text-black">
                {metric.v}
              </p>
            </motion.div>
          ))}
        </div>
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.45 }}
              className="rounded-3xl border border-black/10 bg-white p-6 shadow-sm"
            >
              {d.paragraphs.map((p, idx) => (
                <p
                  key={idx}
                  className={
                    idx === 0
                      ? "text-[16px] leading-8 text-black"
                      : "mt-4 text-[15px] leading-7 text-black/80"
                  }
                >
                  {p}
                </p>
              ))}
            </motion.div>
            {d.bullets.length > 0 && (
              <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                {d.bullets.map((b, idx) => (
                  <motion.li
                    key={idx}
                    initial={false}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.35, delay: idx * 0.04 }}
                    whileHover={{ y: -2 }}
                    className="group flex items-start gap-3 rounded-xl border border-black/10 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/35 hover:shadow-md"
                  >
                    <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/5 text-black">
                      <Icon icon="ph:check" className="h-4 w-4" />
                    </span>
                    <span className="text-sm text-black/80">{b}</span>
                  </motion.li>
                ))}
              </ul>
            )}
          </div>
          <div className="lg:col-span-4">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.45, delay: 0.05 }}
              className="rounded-2xl border border-black/10 bg-white p-6 shadow-sm"
            >
              <div className="text-xs uppercase tracking-[0.18em] text-black/60">
                {d.whyTitle}
              </div>
              <ul className="mt-3 space-y-2 text-sm text-black/80">
                {d.whyItems.map((item, idx) => (
                  <li key={item} className="flex items-start gap-2">
                    <Icon
                      icon={WHY_ICONS[idx] || "ph:check"}
                      className="mt-0.5 h-4 w-4"
                    />{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeoContentSection;
